import {
  BG_STYLES,
  BORDER_STYLES,
  Button,
  COMMON_STYLES,
  LazyImage,
  SIZE,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from "@dehaat/dds";
import React, { useEffect, useRef, useState } from "react";
import {
  Image,
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { FallbackIcon } from "./RoundImageWithIcon";
import { EDIT, LEAF } from "../assests/images";
import { Crop, Disease } from "../models/diagnosis";
import { trackEvent } from "app/farmer360/utils/tracking";
import { CustomEventTypes } from "../utils/events/CustomEventTypes";
import { useAppSelector } from "app/hooks/reduxHooks";
import { selectPlantixUId } from "app/farmer360/store/app";
import { useTranslation } from "react-i18next";

export interface ConfirmDiseaseProps {
  crop: Crop;
  diseases: Disease[];
  onBtnClick: (disease: Disease) => void;
  onEdit: () => void;
  onImagePress?: (item: string[]) => void;
}
const ConfirmDisease = ({
  crop,
  diseases,
  onBtnClick,
  onEdit,
  onImagePress,
}: ConfirmDiseaseProps) => {
  const { t } = useTranslation();
  const scrollRef = useRef<ScrollView>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const plantixId = useAppSelector(selectPlantixUId);
  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const index = Math.round(
      event.nativeEvent.contentOffset.x / containerWidth
    );
    setActiveIndex(index);
  };

  const onLayout = (event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setContainerWidth(width - 32);
  };

  useEffect(() => {
    trackEvent(CustomEventTypes.CropDocImageDiseaseSelectionUserLanded, {
      PlantixID: plantixId,
      CropID: crop.crop_id,
      CropName: crop.crop_name_en,
    });
  }, [crop.crop_id, crop.crop_name_en, plantixId]);

  return (
    <View style={[COMMON_STYLES.flex1]}>
      <View
        style={[COMMON_STYLES.ph1, BG_STYLES.bgWhite, COMMON_STYLES["pv1/2"]]}
      >
        <View
          style={[
            COMMON_STYLES["p0.5"],
            COMMON_STYLES.row,
            COMMON_STYLES["br1/2"],
            COMMON_STYLES.alignItemsCenter,
            BG_STYLES.bgNeutral10,
            COMMON_STYLES.relative,
            COMMON_STYLES.b1,
            BORDER_STYLES.borderNeutral20,
          ]}
        >
          <Text
            style={[
              TYPOGRAPHY.TEXT1,
              VARIANTS_STYLES.SEMI_EMPHASIZED,
              TEXT_STYLES.textNeutral80,
            ]}
          >
            {t("selected_crop")} :
          </Text>
          <Text
            style={[
              TYPOGRAPHY.HEADING3,
              VARIANTS_STYLES.EMPHASIZED,
              TEXT_STYLES.textNeutral90,
              COMMON_STYLES["ml0.5"],
            ]}
          >
            {crop?.crop_name_local ? crop?.crop_name_local : ""}
          </Text>
          <Pressable
            style={[styles.editButtonContainer, COMMON_STYLES.absolute]}
            onPress={onEdit}
          >
            <Image source={EDIT} style={{ width: 24, height: 24 }} />
          </Pressable>
        </View>
      </View>
      <View
        style={[
          COMMON_STYLES.p1,
          BG_STYLES.bgWhite,
          { borderTopWidth: 16 },
          BORDER_STYLES.borderNeutral20,
        ]}
      >
        <Text
          style={[
            TYPOGRAPHY.HEADING3,
            VARIANTS_STYLES.EMPHASIZED,
            COMMON_STYLES["mb0.5"],
          ]}
        >
          {t("suspected_diseases")}
        </Text>
        <Text
          style={[TYPOGRAPHY.TEXT1, VARIANTS_STYLES.BASE, COMMON_STYLES.mb1]}
        >
          {t("check_crop_disease")}
        </Text>
        {diseases.map((item) => {
          return (
            <View
              style={[
                COMMON_STYLES.p1,
                COMMON_STYLES.br1,
                COMMON_STYLES.b1,
                BORDER_STYLES.borderNeutral40,
                COMMON_STYLES["mb3/2"],
                { gap: 12 },
              ]}
              onLayout={onLayout}
            >
              <Text
                style={[
                  TYPOGRAPHY.HEADING2,
                  VARIANTS_STYLES.EMPHASIZED,
                  TEXT_STYLES.textBlack,
                ]}
              >
                {item.disease_name}
              </Text>
              <View style={[COMMON_STYLES.row, COMMON_STYLES.alignItemsCenter]}>
                <Image
                  source={LEAF}
                  style={[COMMON_STYLES["mr0.5"], { width: 24, height: 24 }]}
                />
                <Text
                  style={[TYPOGRAPHY.TEXT1, VARIANTS_STYLES.SEMI_EMPHASIZED]}
                >
                  {t("symptoms")}
                </Text>
              </View>
              {item.symptoms.map((symptoms) => {
                return (
                  <View
                    style={[COMMON_STYLES.row, COMMON_STYLES.alignItemsCenter]}
                  >
                    <Text
                      style={{ fontSize: 20, lineHeight: 24, marginRight: 6 }}
                    >
                      •
                    </Text>
                    <Text style={[TYPOGRAPHY.TEXT1, VARIANTS_STYLES.BASE]}>
                      {symptoms}
                    </Text>
                  </View>
                );
              })}

              {containerWidth > 0 && (
                <Pressable
                  onPress={() => {
                    onImagePress?.(item.image_refs);
                    trackEvent(CustomEventTypes.CropDocImageViewed, {
                      PlantixID: plantixId,
                    });
                  }}
                >
                  <ScrollView
                    ref={scrollRef}
                    horizontal
                    pagingEnabled
                    onScroll={handleScroll}
                    scrollEventThrottle={16}
                    showsHorizontalScrollIndicator={false}
                    style={[styles.scrollView, { width: containerWidth }]}
                  >
                    {item.image_refs.map((image, index) => (
                      <LazyImage
                        imageUrl={image}
                        fallBackIcon={
                          <FallbackIcon
                            text={item.disease_name}
                            containerStyle={[
                              styles.image,
                              { width: containerWidth },
                            ]}
                          />
                        }
                        imgStyles={[styles.image, { width: containerWidth }]}
                      />
                    ))}
                  </ScrollView>

                  <View style={styles.dotsContainer}>
                    {item.image_refs.map((_, index) => (
                      <View
                        key={index}
                        style={[
                          styles.dot,
                          activeIndex === index && styles.activeDot,
                        ]}
                      />
                    ))}
                  </View>
                </Pressable>
              )}

              <Button
                text={t("see_diagnosis")}
                onClick={() => {
                  if (onBtnClick) {
                    onBtnClick(item);
                  }
                }}
                size={SIZE.large}
              />
            </View>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  editButtonContainer: {
    right: 10,
  },
  scrollView: {
    height: 200,
  },
  image: {
    height: 200,
    borderRadius: 12,
    marginRight: 8,
  },
  dotsContainer: {
    flexDirection: "row",
    justifyContent: "center",
    marginTop: 8,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#ccc",
    marginHorizontal: 4,
  },
  activeDot: {
    backgroundColor: "#000",
  },
});

export default ConfirmDisease;
